import React, { useState, useEffect } from 'react';
import { X, Save, DollarSign, Target, Award, Tag, Calendar } from 'lucide-react';
import './EditBountyModal.css';
import MarkdownEditor from './MarkdownEditor';

interface EditBountyModalProps {
  bounty: {
    id: string;
    title: string;
    description: string;
    value: number;
    currency: string;
    status: string;
    type: string;
    difficulty: string;
    requirements: string[];
    tags: string[];
    maxWinners: number;
    currentWinners: number;
    startDate?: Date | string;
    endDate?: Date | string;
    sponsorName?: string;
    sponsorWebsite?: string;
    sponsorLogo?: string;
    swag?: string;
  };
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
}

const EditBountyModal: React.FC<EditBountyModalProps> = ({ bounty, onClose, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: bounty.title || '',
    description: bounty.description || '',
    value: bounty.value || 0,
    currency: bounty.currency || 'USD',
    status: bounty.status || 'draft',
    type: bounty.type || 'other',
    difficulty: bounty.difficulty || 'beginner',
    requirements: bounty.requirements?.join(', ') || '',
    tags: bounty.tags?.join(', ') || '',
    maxWinners: bounty.maxWinners || 1,
    startDate: formatDateForInput(bounty.startDate),
    endDate: formatDateForInput(bounty.endDate),
    sponsorName: bounty.sponsorName || '',
    sponsorWebsite: bounty.sponsorWebsite || '',
    sponsorLogo: bounty.sponsorLogo || '',
    swag: bounty.swag || '',
  });

  function formatDateForInput(date?: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Convert comma-separated strings to arrays
      const requirements = formData.requirements
        .split(',')
        .map(r => r.trim())
        .filter(r => r.length > 0);

      const tags = formData.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const dataToSave: any = {
        title: formData.title,
        description: formData.description,
        value: formData.value,
        currency: formData.currency,
        status: formData.status,
        type: formData.type,
        difficulty: formData.difficulty,
        maxWinners: formData.maxWinners,
        requirements,
        tags,
        sponsorName: formData.sponsorName,
        sponsorWebsite: formData.sponsorWebsite,
        sponsorLogo: formData.sponsorLogo,
        swag: formData.swag,
      };

      // Only include dates if they have values
      if (formData.startDate) {
        dataToSave.startDate = formData.startDate;
      }
      if (formData.endDate) {
        dataToSave.endDate = formData.endDate;
      }

      await onSave(dataToSave);
    } catch (error) {
      console.error('Error saving bounty:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="edit-bounty-modal-overlay" onClick={onClose}>
      <div className="edit-bounty-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="edit-bounty-modal-header">
          <h2 className="edit-bounty-modal-title">Edit Bounty</h2>
          <button
            onClick={onClose}
            className="edit-bounty-modal-close"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="edit-bounty-modal-form">
          {/* Basic Information Section */}
          <div className="edit-bounty-form-section">
            <h3 className="edit-bounty-section-title">
              <Award className="h-5 w-5" />
              Basic Information
            </h3>

            <div className="edit-bounty-form-grid">
              <div className="edit-bounty-form-group edit-bounty-form-full">
                <label className="edit-bounty-form-label">
                  Bounty Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="edit-bounty-form-input"
                  placeholder="Enter bounty title"
                />
              </div>

              <div className="edit-bounty-form-group edit-bounty-form-full">
                <label className="edit-bounty-form-label">
                  Description
                </label>
                <MarkdownEditor
                  value={formData.description}
                  onChange={(value) => handleChange('description', value)}
                  placeholder="Describe the bounty. Use **bold**, *italic*, [links](url), and - bullet lists."
                  height={200}
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Value
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.value}
                  onChange={(e) => handleChange('value', parseInt(e.target.value))}
                  className="edit-bounty-form-input"
                  placeholder="0"
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Currency
                </label>
                <input
                  type="text"
                  value={formData.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  className="edit-bounty-form-input"
                  placeholder="USD"
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="edit-bounty-form-select"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  <Target className="h-4 w-4 inline mr-1" />
                  Max Winners
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxWinners}
                  onChange={(e) => handleChange('maxWinners', parseInt(e.target.value))}
                  className="edit-bounty-form-input"
                />
              </div>
            </div>
          </div>

          {/* Type & Difficulty Section */}
          <div className="edit-bounty-form-section">
            <h3 className="edit-bounty-section-title">
              <Tag className="h-5 w-5" />
              Type & Difficulty
            </h3>

            <div className="edit-bounty-form-grid">
              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="edit-bounty-form-select"
                >
                  <option value="bug_fix">Bug Fix</option>
                  <option value="feature">Feature</option>
                  <option value="integration">Integration</option>
                  <option value="optimization">Optimization</option>
                  <option value="documentation">Documentation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Difficulty
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => handleChange('difficulty', e.target.value)}
                  className="edit-bounty-form-select"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
            </div>
          </div>

          {/* Requirements & Tags Section */}
          <div className="edit-bounty-form-section">
            <h3 className="edit-bounty-section-title">
              <Target className="h-5 w-5" />
              Requirements & Tags
            </h3>

            <div className="edit-bounty-form-grid">
              <div className="edit-bounty-form-group edit-bounty-form-full">
                <label className="edit-bounty-form-label">
                  Requirements
                </label>
                <div className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-400 focus-within:border-transparent flex flex-wrap gap-1.5 items-center bg-white">
                  {formData.requirements.split(',').filter(r => r.trim()).map((requirement, index) => {
                    const trimmedReq = requirement.trim();
                    return (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(trimmedReq.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}
                      >
                        {trimmedReq}
                        <button
                          type="button"
                          onClick={() => {
                            const reqs = formData.requirements.split(',').filter(r => r.trim()).filter((_, i) => i !== index);
                            handleChange('requirements', reqs.join(', '));
                          }}
                          className="ml-1 hover:opacity-70 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                  <input
                    type="text"
                    onKeyDown={(e) => {
                      const input = e.currentTarget;
                      const value = input.value;
                      if (e.key === ' ' && value.trim()) {
                        e.preventDefault();
                        const existingReqs = formData.requirements ? formData.requirements.split(',').filter(r => r.trim()) : [];
                        const newReqs = [...existingReqs, value.trim()];
                        handleChange('requirements', newReqs.join(', '));
                        input.value = '';
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (value.trim()) {
                          const existingReqs = formData.requirements ? formData.requirements.split(',').filter(r => r.trim()) : [];
                          const newReqs = [...existingReqs, value.trim()];
                          handleChange('requirements', newReqs.join(', '));
                          input.value = '';
                        }
                      } else if (e.key === 'Backspace' && !value) {
                        const reqs = formData.requirements.split(',').filter(r => r.trim());
                        if (reqs.length > 0) {
                          reqs.pop();
                          handleChange('requirements', reqs.join(', '));
                        }
                      }
                    }}
                    className="flex-1 min-w-[120px] outline-none bg-transparent"
                    placeholder={!formData.requirements || formData.requirements.split(',').filter(r => r.trim()).length === 0 ? "Type and press space to add requirements..." : ""}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Press space or enter to create a requirement. Backspace to remove the last requirement.</p>
              </div>

              <div className="edit-bounty-form-group edit-bounty-form-full">
                <label className="edit-bounty-form-label">
                  Tags
                </label>
                <div className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-400 focus-within:border-transparent flex flex-wrap gap-1.5 items-center bg-white">
                  {formData.tags.split(',').filter(t => t.trim()).map((tag, index) => {
                    const trimmedTag = tag.trim();
                    return (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(trimmedTag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}
                      >
                        {trimmedTag}
                        <button
                          type="button"
                          onClick={() => {
                            const tags = formData.tags.split(',').filter(t => t.trim()).filter((_, i) => i !== index);
                            handleChange('tags', tags.join(', '));
                          }}
                          className="ml-1 hover:opacity-70 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                  <input
                    type="text"
                    onKeyDown={(e) => {
                      const input = e.currentTarget;
                      const value = input.value;
                      if (e.key === ' ' && value.trim()) {
                        e.preventDefault();
                        const existingTags = formData.tags ? formData.tags.split(',').filter(t => t.trim()) : [];
                        const newTags = [...existingTags, value.trim()];
                        handleChange('tags', newTags.join(', '));
                        input.value = '';
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (value.trim()) {
                          const existingTags = formData.tags ? formData.tags.split(',').filter(t => t.trim()) : [];
                          const newTags = [...existingTags, value.trim()];
                          handleChange('tags', newTags.join(', '));
                          input.value = '';
                        }
                      } else if (e.key === 'Backspace' && !value) {
                        const tags = formData.tags.split(',').filter(t => t.trim());
                        if (tags.length > 0) {
                          tags.pop();
                          handleChange('tags', tags.join(', '));
                        }
                      }
                    }}
                    className="flex-1 min-w-[120px] outline-none bg-transparent"
                    placeholder={!formData.tags || formData.tags.split(',').filter(t => t.trim()).length === 0 ? "Type and press space to add tags..." : ""}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Press space or enter to create a tag. Backspace to remove the last tag.</p>
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className="edit-bounty-form-section">
            <h3 className="edit-bounty-section-title">
              <Calendar className="h-5 w-5" />
              Dates
            </h3>

            <div className="edit-bounty-form-grid">
              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="edit-bounty-form-input"
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="edit-bounty-form-input"
                />
              </div>
            </div>
          </div>



          {/* Sponsor Information (Optional) */}
          <div className="edit-bounty-form-section">
            <h3 className="edit-bounty-section-title">
              <Award className="h-5 w-5" />
              Sponsor Information (Optional)
            </h3>

            <div className="edit-bounty-form-grid">
              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Sponsor Name
                </label>
                <input
                  type="text"
                  value={formData.sponsorName}
                  onChange={(e) => handleChange('sponsorName', e.target.value)}
                  className="edit-bounty-form-input"
                  placeholder="Company or organization name"
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Sponsor Website
                </label>
                <input
                  type="url"
                  value={formData.sponsorWebsite}
                  onChange={(e) => handleChange('sponsorWebsite', e.target.value)}
                  className="edit-bounty-form-input"
                  placeholder="https://..."
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Sponsor Logo URL
                </label>
                <input
                  type="url"
                  value={formData.sponsorLogo}
                  onChange={(e) => handleChange('sponsorLogo', e.target.value)}
                  className="edit-bounty-form-input"
                  placeholder="https://... (image URL)"
                />
              </div>

              <div className="edit-bounty-form-group">
                <label className="edit-bounty-form-label">
                  Non-Cash Prizes (Swag)
                </label>
                <input
                  type="text"
                  value={formData.swag}
                  onChange={(e) => handleChange('swag', e.target.value)}
                  className="edit-bounty-form-input"
                  placeholder="e.g., Software licenses, mentorship, swag"
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="edit-bounty-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="edit-bounty-btn edit-bounty-btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-bounty-btn edit-bounty-btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="edit-bounty-btn-loading">
                  <div className="edit-bounty-spinner"></div>
                  Saving...
                </span>
              ) : (
                <span className="edit-bounty-btn-content">
                  <Save className="h-4 w-4" />
                  Save Changes
                </span>
              )}
            </button>
          </div>
        </form>
      </div >
    </div >
  );
};

export default EditBountyModal;

